import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';

import { JiraProjectCredential } from '../../models/JiraProjectCredential';
import { JiraEmployeeCredential } from '../../models/jiraEmployeeCredential';
import { JiraLoginService } from '../../shared/login-components/jira-login/jira-login.service';
import { TempoService } from '../tempo.service';

interface AccountWithIssues {
    id: number;
    name: string;
    issueKeys: string[];
}

interface ProjectWithAccounts {
    project: JiraProjectCredential;
    accounts?: AccountWithIssues[];
    loaded?: boolean;
}

@Component({
    selector: 'app-jira-projects-overview',
    templateUrl: './jira-projects-overview.component.html',
    styleUrls: ['./jira-projects-overview.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class JiraProjectsOverviewComponent implements OnInit, OnDestroy {
    loading = true;
    error: string | null = null;
    projects: JiraProjectCredential[] = [];
    results: ProjectWithAccounts[] = [];
    employees: JiraEmployeeCredential[] = [];
    private sub = new Subscription();

    constructor(private jiraLoginService: JiraLoginService, private tempoService: TempoService) {}

    ngOnInit(): void {
        const s = this.jiraLoginService.getJiraProjectKeys().subscribe({
            next: (projects) => {
                this.projects = (projects as any[]).map((p) => new JiraProjectCredential(p.projectName, p.projectKey, p.projectId));
                this.results = this.projects.map((p) => ({ project: p, loaded: false }));
                // Fetch Jira accounts (users)
                const empSub = this.jiraLoginService.getJiraEmployeeIDs().subscribe({
                    next: (employees) => {
                        this.employees = (employees as any[]).map((e) => new JiraEmployeeCredential(e.employeeName, e.employeeId ?? e.jiraEmployeeId));
                    },
                    error: () => {
                        this.error = 'Kon Jira accounts niet laden.';
                    },
                });
                this.sub.add(empSub);
                this.loading = false;
            },
            error: () => {
                this.error = 'Kon Jira projecten niet laden.';
                this.loading = false;
            },
        });
        this.sub.add(s);
    }

    loadAccountsForProject(item: ProjectWithAccounts) {
        if (item.loaded) return;
        const sub = this.tempoService.fetchAccounts().subscribe({
            next: (accounts) => {
                const linksSub = this.tempoService.fetchAccountLinksByProject(item.project.jiraKey).subscribe({
                    next: (links) => {
                        const accountsForProj: Record<number, AccountWithIssues> = {};
                        links.forEach((l) => {
                            const acc = accounts.find((a) => a.id === l.accountId);
                            const issueKey = l.issueKey || '';
                            if (!acc) return;
                            if (!accountsForProj[acc.id]) {
                                accountsForProj[acc.id] = { id: acc.id, name: acc.name, issueKeys: [] };
                            }
                            if (issueKey && !accountsForProj[acc.id].issueKeys.includes(issueKey)) {
                                accountsForProj[acc.id].issueKeys.push(issueKey);
                            }
                        });
                        item.accounts = Object.values(accountsForProj);
                        item.loaded = true;
                    },
                    error: () => {
                        this.error = 'Kon account-links niet ophalen van Tempo.';
                    },
                });
                this.sub.add(linksSub);
            },
            error: () => {
                this.error = 'Kon Tempo accounts niet laden.';
            },
        });
        this.sub.add(sub);
    }

    ngOnDestroy(): void {
        this.sub.unsubscribe();
    }
}
